import { useProjects, useUpdateProject } from '@/hooks'
import { useUIStore } from '@/stores'
import { useCreateProject } from '@/hooks'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { api } from '@/services/api-client'
import { Plus, Zap, FolderOpen, MessageSquare, Clock, LayoutGrid, List, Film, Pencil, ImagePlus, X, Crop, Check, Search, Folder, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { formatRelativeTime, truncate } from '@/utils/helpers'
import { parseTags } from '@/types'
import { useT } from '@/i18n'
import type { Project } from '@/types'

type DashViewMode = 'cards' | 'list' | 'movie'
type DashSortKey = 'name' | 'modified' | 'created'
type DashSortDir = 'asc' | 'desc'

function getStoredDashView(): DashViewMode {
  try { return (localStorage.getItem('dash-view') as DashViewMode) || 'movie' } catch { return 'movie' }
}
function getStoredSort(): { key: DashSortKey; dir: DashSortDir } {
  try {
    const raw = localStorage.getItem('dash-sort')
    if (raw) return JSON.parse(raw)
  } catch {}
  return { key: 'modified', dir: 'desc' }
}

const PRESET_COLORS = [
  '#58a6ff', '#a855f7', '#f97316', '#22c55e', '#ef4444',
  '#eab308', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6',
]

// ── Crop Modal ────────────────────────────────────────────────────────────────

type CropBox = { x: number; y: number; w: number; h: number }

function CropModal({ file, onConfirm, onCancel }: {
  file: File
  onConfirm: (dataUrl: string) => void
  onCancel: () => void
}) {
  const [imgSrc, setImgSrc] = useState('')
  const [crop, setCrop] = useState<CropBox | null>(null)
  const [saving, setSaving] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ startX: number; startY: number; origCrop: CropBox; type: string } | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setImgSrc(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const initCrop = useCallback(() => {
    const img = imgRef.current
    const cont = containerRef.current
    if (!img || !cont) return
    const ir = img.getBoundingClientRect()
    const cr = cont.getBoundingClientRect()
    const ox = ir.left - cr.left
    const oy = ir.top - cr.top
    const iw = ir.width
    const ih = ir.height
    // Fit a 2:3 box (poster format) into 80% of image area
    let w = iw * 0.85
    let h = w * (3 / 2)
    if (h > ih * 0.85) { h = ih * 0.85; w = h * (2 / 3) }
    setCrop({ x: ox + (iw - w) / 2, y: oy + (ih - h) / 2, w, h })
  }, [])

  // Global mouse handlers for dragging
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragRef.current || !crop) return
      const { startX, startY, origCrop, type } = dragRef.current
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      const img = imgRef.current
      const cont = containerRef.current
      if (!img || !cont) return
      const ir = img.getBoundingClientRect()
      const cr = cont.getBoundingClientRect()
      const minX = ir.left - cr.left
      const minY = ir.top - cr.top
      const maxX = minX + ir.width
      const maxY = minY + ir.height

      if (type === 'move') {
        const nx = Math.max(minX, Math.min(maxX - origCrop.w, origCrop.x + dx))
        const ny = Math.max(minY, Math.min(maxY - origCrop.h, origCrop.y + dy))
        setCrop(p => p ? { ...p, x: nx, y: ny } : p)
      } else {
        // Resize from corner — maintain 2:3
        let nw = origCrop.w, nh = origCrop.h, nx = origCrop.x, ny = origCrop.y
        if (type === 'br') {
          nw = Math.max(60, origCrop.w + dx)
          nh = nw * (3 / 2)
          nw = Math.min(nw, maxX - origCrop.x)
          nh = Math.min(nh, maxY - origCrop.y)
          nw = nh * (2 / 3)
        } else if (type === 'bl') {
          nw = Math.max(60, origCrop.w - dx)
          nh = nw * (3 / 2)
          nw = Math.min(nw, origCrop.x + origCrop.w - minX)
          nh = Math.min(nh, maxY - origCrop.y)
          nw = nh * (2 / 3)
          nx = origCrop.x + origCrop.w - nw
        } else if (type === 'tr') {
          nw = Math.max(60, origCrop.w + dx)
          nh = nw * (3 / 2)
          nw = Math.min(nw, maxX - origCrop.x)
          nh = Math.min(nh, origCrop.y + origCrop.h - minY)
          nw = nh * (2 / 3)
          ny = origCrop.y + origCrop.h - nh
        } else if (type === 'tl') {
          nw = Math.max(60, origCrop.w - dx)
          nh = nw * (3 / 2)
          nw = Math.min(nw, origCrop.x + origCrop.w - minX)
          nh = Math.min(nh, origCrop.y + origCrop.h - minY)
          nw = nh * (2 / 3)
          nx = origCrop.x + origCrop.w - nw
          ny = origCrop.y + origCrop.h - nh
        }
        setCrop({ x: nx, y: ny, w: nw, h: nh })
      }
    }
    const up = () => { dragRef.current = null }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up) }
  }, [crop])

  const startDrag = (e: React.MouseEvent, type: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (!crop) return
    dragRef.current = { startX: e.clientX, startY: e.clientY, origCrop: { ...crop }, type }
  }

  const handleConfirm = () => {
    const img = imgRef.current
    const cont = containerRef.current
    if (!img || !cont || !crop) return
    setSaving(true)
    const canvas = document.createElement('canvas')
    canvas.width = 400
    canvas.height = 600
    const ctx = canvas.getContext('2d')!
    const ir = img.getBoundingClientRect()
    const cr = cont.getBoundingClientRect()
    const imgOffX = ir.left - cr.left
    const imgOffY = ir.top - cr.top
    const scaleX = img.naturalWidth / ir.width
    const scaleY = img.naturalHeight / ir.height
    const sx = (crop.x - imgOffX) * scaleX
    const sy = (crop.y - imgOffY) * scaleY
    const sw = crop.w * scaleX
    const sh = crop.h * scaleY
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 400, 600)
    onConfirm(canvas.toDataURL('image/jpeg', 0.88))
  }

  const HANDLE_SIZE = 12

  return (
    <div className="crop-overlay" onClick={onCancel}>
      <div className="crop-modal" onClick={e => e.stopPropagation()}>
        <div className="crop-modal-header">
          <div className="crop-modal-title">
            <Crop size={16} />
            <span>Kadruj plakat (proporcje 2:3)</span>
          </div>
          <div className="crop-modal-actions">
            <button className="button-primary" onClick={handleConfirm} disabled={!crop || saving}>
              <Check size={14} /> {saving ? 'Zapisywanie…' : 'Przytnij i zapisz'}
            </button>
            <button className="button-secondary" onClick={onCancel}>Anuluj</button>
          </div>
        </div>
        <div ref={containerRef} className="crop-container">
          {imgSrc && (
            <img
              ref={imgRef}
              src={imgSrc}
              className="crop-img"
              onLoad={initCrop}
              draggable={false}
            />
          )}
          {crop && (
            <div className="crop-overlay-mask" style={{ '--cx': `${crop.x}px`, '--cy': `${crop.y}px`, '--cw': `${crop.w}px`, '--ch': `${crop.h}px` } as React.CSSProperties}>
              {/* Mask quadrants */}
              <div className="crop-mask-piece" style={{ top: 0, left: 0, right: 0, height: crop.y }} />
              <div className="crop-mask-piece" style={{ top: crop.y + crop.h, left: 0, right: 0, bottom: 0 }} />
              <div className="crop-mask-piece" style={{ top: crop.y, left: 0, width: crop.x, height: crop.h }} />
              <div className="crop-mask-piece" style={{ top: crop.y, left: crop.x + crop.w, right: 0, height: crop.h }} />
              {/* Crop box */}
              <div
                className="crop-box"
                style={{ left: crop.x, top: crop.y, width: crop.w, height: crop.h }}
                onMouseDown={e => startDrag(e, 'move')}
              >
                {/* Rule of thirds grid */}
                <div className="crop-grid-h" style={{ top: '33.3%' }} />
                <div className="crop-grid-h" style={{ top: '66.6%' }} />
                <div className="crop-grid-v" style={{ left: '33.3%' }} />
                <div className="crop-grid-v" style={{ left: '66.6%' }} />
                {/* Corner handles */}
                {(['tl', 'tr', 'bl', 'br'] as const).map(corner => (
                  <div
                    key={corner}
                    className={`crop-handle crop-handle-${corner}`}
                    style={{ width: HANDLE_SIZE, height: HANDLE_SIZE }}
                    onMouseDown={e => startDrag(e, corner)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export function Dashboard() {
  const t = useT()
  const { navigateToProject, navigateToThread } = useUIStore()
  const { data: projects, isLoading: projectsLoading } = useProjects()
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()
  const [showNewProject, setShowNewProject] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDesc, setNewProjectDesc] = useState('')
  const [viewMode, setViewMode] = useState<DashViewMode>(getStoredDashView)
  const [editingColor, setEditingColor] = useState<string | null>(null)
  const [cropState, setCropState] = useState<{ projectId: string; file: File } | null>(null)
  const posterInputRef = useRef<HTMLInputElement>(null)
  const pendingProjectId = useRef<string | null>(null)
  const [sort, setSort] = useState<{ key: DashSortKey; dir: DashSortDir }>(getStoredSort)

  const setProjectSort = (key: DashSortKey) => {
    setSort(prev => {
      const next = prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' as DashSortDir } : { key, dir: key === 'name' ? 'asc' : 'desc' as DashSortDir }
      try { localStorage.setItem('dash-sort', JSON.stringify(next)) } catch {}
      return next
    })
  }

  const sortedProjects = useMemo(() => {
    if (!projects) return []
    return [...projects].sort((a, b) => {
      let cmp = 0
      if (sort.key === 'name') {
        cmp = a.name.localeCompare(b.name)
      } else if (sort.key === 'modified') {
        cmp = (a.last_activity || a.updated_at || '').localeCompare(b.last_activity || b.updated_at || '')
      } else if (sort.key === 'created') {
        cmp = a.created_at.localeCompare(b.created_at)
      }
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [projects, sort])

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<{ projects: any[]; items: any[] } | null>(null)
  const [searchFocused, setSearchFocused] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = (q: string) => {
    setSearchQuery(q)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (!q.trim() || q.trim().length < 2) { setSearchResults(null); return }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.search(q.trim())
        setSearchResults(res)
      } catch { setSearchResults(null) }
    }, 300)
  }

  // Close search on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const showSearchDrop = searchFocused && searchQuery.trim().length >= 2 && searchResults !== null

  const setView = (mode: DashViewMode) => {
    setViewMode(mode)
    try { localStorage.setItem('dash-view', mode) } catch {}
  }

  const handleCreate = () => {
    if (!newProjectName.trim()) return
    createProject.mutate({ name: newProjectName.trim(), description: newProjectDesc.trim() }, {
      onSuccess: () => { setNewProjectName(''); setNewProjectDesc(''); setShowNewProject(false) },
    })
  }

  const handleColorChange = (projectId: string, color: string) => {
    updateProject.mutate({ id: projectId, data: { icon_color: color } })
    setEditingColor(null)
  }

  const openPosterPicker = useCallback((projectId: string) => {
    pendingProjectId.current = projectId
    posterInputRef.current?.click()
  }, [])

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const projectId = pendingProjectId.current
    if (file && projectId) setCropState({ projectId, file })
    if (posterInputRef.current) posterInputRef.current.value = ''
  }

  const handleCropConfirm = (dataUrl: string) => {
    if (!cropState) return
    updateProject.mutate({ id: cropState.projectId, data: { poster_image: dataUrl } })
    setCropState(null)
    pendingProjectId.current = null
  }

  const handleRemovePoster = (projectId: string) => {
    updateProject.mutate({ id: projectId, data: { poster_image: null } })
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>{t.dashTitle}</h1>
          <p className="dashboard-subtitle">{t.dashSubtitle}</p>
        </div>
        <div className="dashboard-header-actions">
          {/* Search */}
          <div className="dash-search-wrap" ref={searchRef}>
            <div className="dash-search">
              <Search size={14} />
              <input
                className="dash-search-input"
                placeholder="Szukaj projektów, folderów, wątków…"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => setSearchFocused(true)}
              />
              {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults(null) }}><X size={12} /></button>}
            </div>
            {showSearchDrop && (
              <div className="dash-search-results">
                {searchResults!.projects.length === 0 && searchResults!.items.length === 0 && (
                  <div className="dash-search-empty">Brak wyników</div>
                )}
                {searchResults!.projects.length > 0 && (
                  <div className="dash-search-group">
                    <div className="dash-search-group-label">Projekty</div>
                    {searchResults!.projects.map((p: any) => (
                      <button key={p.id} className="dash-search-item" onClick={() => { navigateToProject(p.id); setSearchFocused(false); setSearchQuery(''); setSearchResults(null) }}>
                        <span className="dash-search-dot" style={{ backgroundColor: p.icon_color }} />
                        <span className="dash-search-name">{p.name}</span>
                        {p.description && <span className="dash-search-desc">{p.description}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {searchResults!.items.length > 0 && (
                  <div className="dash-search-group">
                    <div className="dash-search-group-label">Foldery i wątki</div>
                    {searchResults!.items.map((item: any) => (
                      <button key={item.id} className="dash-search-item" onClick={() => {
                        if (item.type === 'thread') { navigateToThread(item.id, item.project_id) }
                        else { navigateToProject(item.project_id) }
                        setSearchFocused(false); setSearchQuery(''); setSearchResults(null)
                      }}>
                        {item.type === 'folder' ? <Folder size={12} className="dash-search-type-icon" /> : <MessageSquare size={12} className="dash-search-type-icon" />}
                        <span className="dash-search-name">{item.name}</span>
                        <span className="dash-search-meta">{item.project_name}{item.folder_name ? ` / ${item.folder_name}` : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="dash-sort-wrap">
            <ArrowUpDown size={13} className="dash-sort-icon" />
            <button className={`dash-sort-btn${sort.key === 'name' ? ' active' : ''}`} onClick={() => setProjectSort('name')}>
              A-Z {sort.key === 'name' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
            </button>
            <button className={`dash-sort-btn${sort.key === 'modified' ? ' active' : ''}`} onClick={() => setProjectSort('modified')}>
              Modyfikacja {sort.key === 'modified' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
            </button>
            <button className={`dash-sort-btn${sort.key === 'created' ? ' active' : ''}`} onClick={() => setProjectSort('created')}>
              Dodanie {sort.key === 'created' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
            </button>
          </div>
          <div className="dash-view-toggle">
            <button className={`dash-view-btn${viewMode === 'cards' ? ' active' : ''}`} onClick={() => setView('cards')} title="Widok kart"><LayoutGrid size={15} /></button>
            <button className={`dash-view-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setView('list')} title="Widok listy"><List size={15} /></button>
            <button className={`dash-view-btn${viewMode === 'movie' ? ' active' : ''}`} onClick={() => setView('movie')} title="Widok filmowy"><Film size={15} /></button>
          </div>
          <button className="button-primary" onClick={() => setShowNewProject(true)}>
            <Plus size={16} /> {t.dashNewProject}
          </button>
        </div>
      </div>

      {showNewProject && (
        <div className="dashboard-new-project">
          <input autoFocus value={newProjectName}
            onChange={e => setNewProjectName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNewProject(false) }}
            placeholder={t.dashProjectName} className="new-project-input" />
          <input value={newProjectDesc}
            onChange={e => setNewProjectDesc(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNewProject(false) }}
            placeholder={t.dashDescription} className="new-project-input" />
          <div className="new-project-actions">
            <button className="button-primary" onClick={handleCreate}>{t.create}</button>
            <button className="button-secondary" onClick={() => setShowNewProject(false)}>{t.cancel}</button>
          </div>
        </div>
      )}

      {/* Color picker */}
      {editingColor && (
        <div className="color-picker-overlay" onClick={() => setEditingColor(null)}>
          <div className="color-picker-popup" onClick={e => e.stopPropagation()}>
            <div className="color-picker-presets">
              {PRESET_COLORS.map(c => (
                <button key={c} className="color-preset-btn" style={{ backgroundColor: c }}
                  onClick={() => handleColorChange(editingColor, c)} />
              ))}
            </div>
            <input type="color" className="color-custom-input"
              onChange={e => handleColorChange(editingColor, e.target.value)} />
          </div>
        </div>
      )}

      {/* Crop modal */}
      {cropState && (
        <CropModal
          file={cropState.file}
          onConfirm={handleCropConfirm}
          onCancel={() => { setCropState(null); pendingProjectId.current = null }}
        />
      )}

      {/* Hidden file input */}
      <input ref={posterInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={handleFileSelected} />

      <div className="dashboard-grid">
        <section className="dashboard-section dashboard-section-projects">
          <h2><FolderOpen size={18} /> {t.navProjects}</h2>
          {projectsLoading && <p className="loading-text">{t.loading}</p>}

          {viewMode === 'cards' && (
            <div className="project-list">
              {sortedProjects.map(project => {
                const tags = parseTags(project.tags)
                return (
                  <div key={project.id} className="project-card" onClick={() => navigateToProject(project.id)}>
                    <div className="project-card-header">
                      <div className="project-card-title">
                        <button className="project-color-dot" style={{ backgroundColor: project.icon_color }}
                          onClick={e => { e.stopPropagation(); setEditingColor(project.id) }} title="Zmień kolor" />
                        <h3>{project.name}</h3>
                      </div>
                      <span className={`status-badge status-${project.status}`}>{project.status}</span>
                    </div>
                    {project.description && <p className="project-card-desc">{project.description}</p>}
                    <div className="project-card-footer">
                      <span><MessageSquare size={12} /> {project.thread_count || 0} {t.threads}</span>
                      <span><FolderOpen size={12} /> {project.folder_count || 0} {t.folders}</span>
                      {project.last_activity && <span><Clock size={12} /> {formatRelativeTime(project.last_activity)}</span>}
                    </div>
                    {tags.length > 0 && (
                      <div className="project-card-tags">{tags.map(tag => <span key={tag} className="tag">{tag}</span>)}</div>
                    )}
                  </div>
                )
              })}
              {!projectsLoading && (!projects || projects.length === 0) && (
                <div className="empty-state"><Zap size={32} /><p>{t.dashNoProjects}</p></div>
              )}
            </div>
          )}

          {viewMode === 'list' && (
            <div className="dash-list-view">
              <div className="dash-list-header">
                <button className={`dll-name dll-sort-col${sort.key === 'name' ? ' sorted' : ''}`} onClick={() => setProjectSort('name')}>
                  Projekt {sort.key === 'name' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                </button>
                <span className="dll-threads">Wątki</span>
                <span className="dll-folders">Foldery</span>
                <button className={`dll-activity dll-sort-col${sort.key === 'modified' ? ' sorted' : ''}`} onClick={() => setProjectSort('modified')}>
                  Aktywność {sort.key === 'modified' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                </button>
                <button className={`dll-created dll-sort-col${sort.key === 'created' ? ' sorted' : ''}`} onClick={() => setProjectSort('created')}>
                  Dodano {sort.key === 'created' && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                </button>
                <span className="dll-status">Status</span>
              </div>
              {sortedProjects.map(project => (
                <div key={project.id} className="dash-list-row" onClick={() => navigateToProject(project.id)}>
                  <span className="dll-name">
                    <button className="project-color-dot" style={{ backgroundColor: project.icon_color, flexShrink: 0 }}
                      onClick={e => { e.stopPropagation(); setEditingColor(project.id) }} title="Zmień kolor" />
                    <span>{project.name}</span>
                    {project.description && <span className="dll-desc">{truncate(project.description, 60)}</span>}
                  </span>
                  <span className="dll-threads">{project.thread_count || 0}</span>
                  <span className="dll-folders">{project.folder_count || 0}</span>
                  <span className="dll-activity">{project.last_activity ? formatRelativeTime(project.last_activity) : '—'}</span>
                  <span className="dll-created">{project.created_at ? formatRelativeTime(project.created_at) : '—'}</span>
                  <span className="dll-status"><span className={`status-badge status-${project.status}`}>{project.status}</span></span>
                </div>
              ))}
              {!projectsLoading && (!projects || projects.length === 0) && (
                <div className="empty-state small"><p>{t.dashNoProjects}</p></div>
              )}
            </div>
          )}

          {viewMode === 'movie' && (
            <div className="dash-movie-view">
              {sortedProjects.map(project => (
                <MoviePoster
                  key={project.id}
                  project={project}
                  onSelect={() => navigateToProject(project.id)}
                  onEditColor={e => { e.stopPropagation(); setEditingColor(project.id) }}
                  onEditPoster={e => { e.stopPropagation(); openPosterPicker(project.id) }}
                  onRemovePoster={e => { e.stopPropagation(); handleRemovePoster(project.id) }}
                />
              ))}
              {!projectsLoading && (!projects || projects.length === 0) && (
                <div className="empty-state"><Zap size={32} /><p>{t.dashNoProjects}</p></div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function MoviePoster({ project, onSelect, onEditColor, onEditPoster, onRemovePoster }: {
  project: Project
  onSelect: () => void
  onEditColor: (e: React.MouseEvent) => void
  onEditPoster: (e: React.MouseEvent) => void
  onRemovePoster: (e: React.MouseEvent) => void
}) {
  const hasPoster = !!project.poster_image
  return (
    <div className="movie-poster-card" onClick={onSelect}>
      <div className="movie-poster-image"
        style={hasPoster
          ? { backgroundImage: `url(${project.poster_image})` }
          : { background: `linear-gradient(160deg, ${project.icon_color}33 0%, ${project.icon_color}99 100%)` }}
      >
        {!hasPoster && (
          <div className="movie-poster-placeholder">
            <div className="movie-poster-initials" style={{ color: project.icon_color }}>
              {project.name.slice(0, 2).toUpperCase()}
            </div>
          </div>
        )}
        <div className="movie-poster-overlay">
          <div className="movie-poster-overlay-actions">
            <button className="movie-poster-action-btn" onClick={onEditPoster} title="Zmień plakat"><ImagePlus size={16} /></button>
            {hasPoster && <button className="movie-poster-action-btn danger" onClick={onRemovePoster} title="Usuń plakat"><X size={16} /></button>}
            <button className="movie-poster-action-btn" onClick={onEditColor} title="Zmień kolor"><Pencil size={16} /></button>
          </div>
        </div>
        <div className="movie-poster-color-bar" style={{ backgroundColor: project.icon_color }} />
      </div>
      <div className="movie-poster-info">
        <h3 className="movie-poster-title">{project.name}</h3>
        <div className="movie-poster-meta">
          <span><MessageSquare size={11} /> {project.thread_count || 0}</span>
          {project.last_activity && <span><Clock size={11} /> {formatRelativeTime(project.last_activity)}</span>}
        </div>
      </div>
    </div>
  )
}
